/**
 * Human-readable descriptions for audit-log entries.
 *
 * The backend logs structured events like:
 *   action:   "roles_changed"
 *   metadata: {old_roles: ["sales"], new_roles: ["sales", "marketing"]}
 *
 * Non-technical readers need to see:
 *   "Roles updated — added Marketing"
 *
 * This module is the single source of truth for that mapping. Adding
 * a new audit action server-side? Add a case here. Unknown actions
 * fall back to a humanised version of the action key, so the drawer
 * still renders something sensible.
 */

import type { AuditLogEntry } from '../../api/admin';

export type Tone = 'success' | 'error' | 'info' | 'warning' | 'muted';

export interface DescribedEvent {
  title: string;
  description: string | null;
  tone: Tone;
}

/** Format a list of role names for readability. */
function fmtRoles(roles: unknown): string {
  if (!Array.isArray(roles) || roles.length === 0) return 'none';
  return roles.map((r) => (typeof r === 'string' ? toTitle(r) : String(r))).join(', ');
}

/** Compute (added, removed) between two role lists. */
function diffRoles(oldArr: unknown, newArr: unknown): { added: string[]; removed: string[] } {
  const a = Array.isArray(oldArr)
    ? new Set(oldArr.filter((x): x is string => typeof x === 'string'))
    : new Set<string>();
  const b = Array.isArray(newArr)
    ? new Set(newArr.filter((x): x is string => typeof x === 'string'))
    : new Set<string>();
  const added = [...b].filter((r) => !a.has(r));
  const removed = [...a].filter((r) => !b.has(r));
  return { added, removed };
}

function toTitle(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function pickStr(meta: Record<string, unknown>, key: string): string | null {
  const v = meta[key];
  return typeof v === 'string' ? v : null;
}

const LOGIN_FAIL_REASONS: Record<string, string> = {
  invalid_credentials: 'Wrong email or password',
  status_pending: 'Account not yet approved',
  status_disabled: 'Account is disabled',
  status_rejected: 'Account was rejected',
};

const PWD_CHANGE_FAIL_REASONS: Record<string, string> = {
  wrong_current_password: 'Current password was incorrect',
};

export function describeAuditEvent(entry: AuditLogEntry): DescribedEvent {
  const meta = (entry.metadata ?? {}) as Record<string, unknown>;

  switch (entry.action) {
    // ── Account lifecycle ────────────────────────────────────────
    case 'signup_submitted':
      return {
        title: 'Signed up',
        description: 'Account request submitted via the public sign-up form.',
        tone: 'info',
      };
    case 'signup_approved':
      return {
        title: 'Sign-up approved',
        description: 'An administrator approved this account.',
        tone: 'success',
      };
    case 'signup_rejected':
      return {
        title: 'Sign-up rejected',
        description: 'An administrator declined this account request.',
        tone: 'error',
      };
    case 'admin_created_user': {
      const roles = fmtRoles(meta.roles);
      return {
        title: 'Account created by administrator',
        description:
          roles === 'none'
            ? 'Created with no roles assigned.'
            : `Created with the following role${Array.isArray(meta.roles) && meta.roles.length > 1 ? 's' : ''}: ${roles}.`,
        tone: 'success',
      };
    }
    case 'seed_admin_bootstrap':
      return {
        title: 'Account created during initial setup',
        description: 'Seeded as part of the platform bootstrap.',
        tone: 'muted',
      };
    case 'account_enabled':
      return {
        title: 'Account re-enabled',
        description: 'An administrator restored access to this account.',
        tone: 'success',
      };
    case 'account_disabled':
      return {
        title: 'Account disabled',
        description: 'An administrator suspended this account.',
        tone: 'warning',
      };

    // ── Sign-in events ────────────────────────────────────────────
    case 'login_success':
      return { title: 'Signed in', description: null, tone: 'success' };
    case 'login_failed': {
      const reason = pickStr(meta, 'reason');
      const friendly = reason ? LOGIN_FAIL_REASONS[reason] : null;
      return {
        title: 'Sign-in failed',
        description: friendly ?? 'A sign-in attempt was rejected.',
        tone: 'error',
      };
    }

    // ── Password events ──────────────────────────────────────────
    case 'password_reset_requested':
      return {
        title: 'Requested a password reset',
        description: 'A "forgot password" email was requested.',
        tone: 'info',
      };
    case 'password_reset_failed':
      return {
        title: 'Password reset link was invalid',
        description: 'The reset link had expired, was already used, or was unknown.',
        tone: 'error',
      };
    case 'password_set': {
      const purpose = pickStr(meta, 'purpose');
      const description =
        purpose === 'set_password'
          ? 'Set their initial password using the invitation link.'
          : purpose === 'forgot_password'
            ? 'Reset their password using the forgot-password link.'
            : 'Set a new password.';
      return { title: 'Password set', description, tone: 'success' };
    }
    case 'password_changed':
      return {
        title: 'Password changed',
        description: 'Changed their password while signed in.',
        tone: 'success',
      };
    case 'password_change_failed': {
      const reason = pickStr(meta, 'reason');
      const friendly = reason ? PWD_CHANGE_FAIL_REASONS[reason] : null;
      return {
        title: 'Password change failed',
        description: friendly ?? 'A password change attempt was rejected.',
        tone: 'error',
      };
    }
    case 'admin_initiated_password_reset':
      return {
        title: 'Administrator reset their password',
        description:
          'An administrator issued a password-reset link. They will be forced to choose a new password.',
        tone: 'warning',
      };
    case 'admin_invitation_resent':
      return {
        title: 'Invitation re-sent by administrator',
        description: 'An administrator issued a fresh set-password link.',
        tone: 'info',
      };

    // ── Roles ────────────────────────────────────────────────────
    case 'roles_changed': {
      const { added, removed } = diffRoles(meta.old_roles, meta.new_roles);
      let description = '';
      if (added.length > 0) {
        description += `Added ${added.map(toTitle).join(', ')}`;
      }
      if (removed.length > 0) {
        if (description) description += '; ';
        description += `Removed ${removed.map(toTitle).join(', ')}`;
      }
      if (!description) {
        description = 'Role assignments updated (no net change).';
      }
      return { title: 'Roles updated', description, tone: 'warning' };
    }
    case 'role_created': {
      const roleName = pickStr(meta, 'role_name');
      return {
        title: 'New role added to catalog',
        description: roleName ? `Created role "${toTitle(roleName)}".` : 'A new role was added.',
        tone: 'success',
      };
    }

    // ── Profile ──────────────────────────────────────────────────
    case 'profile_updated': {
      const oldP = meta.old as Record<string, unknown> | undefined;
      const newP = meta.new as Record<string, unknown> | undefined;
      const oldName = [oldP?.first_name, oldP?.last_name].filter(Boolean).join(' ');
      const newName = [newP?.first_name, newP?.last_name].filter(Boolean).join(' ');
      const description =
        oldName && newName && oldName !== newName
          ? `Name changed from "${oldName}" to "${newName}".`
          : 'Updated their profile.';
      return { title: 'Profile updated', description, tone: 'info' };
    }
    case 'admin_updated_user_profile': {
      const oldP = meta.old as Record<string, unknown> | undefined;
      const newP = meta.new as Record<string, unknown> | undefined;
      const oldName = [oldP?.first_name, oldP?.last_name].filter(Boolean).join(' ');
      const newName = [newP?.first_name, newP?.last_name].filter(Boolean).join(' ');
      const description =
        oldName && newName && oldName !== newName
          ? `An administrator changed the name from "${oldName}" to "${newName}".`
          : 'An administrator edited this profile.';
      return { title: 'Profile updated by administrator', description, tone: 'warning' };
    }

    // ── Data uploads (Admin → Data uploads) ─────────────────────
    case 'data_upload.validated':
      return {
        title: 'Depletions file checked',
        description: `${pickStr(meta, 'filename') ?? 'A file'} passed validation and is awaiting confirmation.`,
        tone: 'info',
      };
    case 'data_upload.rejected':
      return {
        title: 'Depletions file rejected',
        description: `${pickStr(meta, 'filename') ?? 'A file'} failed validation. Nothing was loaded.`,
        tone: 'warning',
      };
    case 'data_upload.confirmed': {
      const targets = Array.isArray(meta.targets)
        ? meta.targets.map((t) => String(t).toUpperCase()).join(' + ')
        : 'the databases';
      return {
        title: 'Depletions load started',
        description: `Confirmed loading ${pickStr(meta, 'filename') ?? 'a file'} into ${targets}.`,
        tone: 'info',
      };
    }
    case 'data_upload.discarded':
      return {
        title: 'Depletions upload discarded',
        description: `${pickStr(meta, 'filename') ?? 'A file'} was discarded without loading.`,
        tone: 'muted',
      };
    case 'data_upload.recipient_added':
      return {
        title: 'Load email recipient added',
        description: `${pickStr(meta, 'email') ?? 'An address'} now gets the depletions load email.`,
        tone: 'info',
      };
    case 'data_upload.recipient_removed':
      return {
        title: 'Load email recipient removed',
        description: `${pickStr(meta, 'email') ?? 'An address'} no longer gets the depletions load email.`,
        tone: 'muted',
      };
    case 'data_upload.finished': {
      const outcome = pickStr(meta, 'outcome');
      const ok = outcome === 'committed' || outcome === 'up_to_date';
      return {
        title: ok ? 'Depletions loaded' : 'Depletions load did not save',
        description: `${pickStr(meta, 'filename') ?? 'A file'}: ${(outcome ?? 'unknown').replace(/_/g, ' ')}.`,
        tone: ok ? 'success' : 'error',
      };
    }

    // ── Unknown / fallback ──────────────────────────────────────
    default:
      return {
        title: toTitle(entry.action),
        description: null,
        tone: 'muted',
      };
  }
}

/** Friendly relative time. Falls back to absolute date past a week. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;

  if (diffMs < 60_000) return 'Just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
